import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Eye, Bell, Store, ChevronRight, Target } from 'lucide-react';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/molecules/sheet';
import { cn } from '@/lib/utils';

interface Objective {
  id: string;
  title: string;
  description: string;
  price: string;
  icon: React.ElementType;
  href: string;
  color: string;
}

const objectives: Objective[] = [
  {
    id: 'boost_sales',
    title: 'Booster les ventes',
    description: 'Produits sponsorisés',
    price: 'À partir de 0.05€/clic',
    icon: TrendingUp,
    href: '/ads/campaigns/new?type=sponsored_product',
    color: 'text-green-600 bg-green-500/10',
  },
  {
    id: 'visibility',
    title: 'Être visible',
    description: 'Carousel ou bannière',
    price: 'À partir de 8€/jour',
    icon: Eye,
    href: '/ads/campaigns/new?objective=visibility',
    color: 'text-blue-600 bg-blue-500/10',
  },
  {
    id: 'notify',
    title: 'Notifier mes clients',
    description: 'Push notifications',
    price: 'À partir de 3€/1000',
    icon: Bell,
    href: '/ads/campaigns/new?objective=notification',
    color: 'text-purple-600 bg-purple-500/10',
  },
  {
    id: 'featured_shop',
    title: 'Mettre en avant ma boutique',
    description: 'Boutique en vedette',
    price: '20€/semaine',
    icon: Store,
    href: '/ads/campaigns/new?type=featured_shop',
    color: 'text-orange-600 bg-orange-500/10',
  },
];

interface ObjectiveBottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ObjectiveBottomSheet({ open, onOpenChange }: ObjectiveBottomSheetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSelectObjective = (href: string) => {
    onOpenChange(false);
    // Small delay to let the sheet close animation play
    setTimeout(() => {
      navigate(href);
    }, 150);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl px-0 pb-8">
        {/* Handle bar */}
        <div className="flex justify-center pt-2 pb-4">
          <div className="w-12 h-1.5 rounded-full bg-muted-foreground/30" />
        </div>

        <SheetHeader className="px-6 pb-4">
          <SheetTitle className="flex items-center gap-2 text-lg">
            <Target className="size-5" />
            {t('ads.objectives.title', 'Quel est votre objectif ?')}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-2 px-4">
          {objectives.map((objective) => {
            const Icon = objective.icon;
            return (
              <button
                key={objective.id}
                onClick={() => handleSelectObjective(objective.href)}
                className={cn(
                  'w-full flex items-center gap-4 p-4 rounded-xl',
                  'bg-card hover:bg-muted/50 active:bg-muted transition-colors',
                  'text-left'
                )}
              >
                <div className={cn('p-3 rounded-xl', objective.color)}>
                  <Icon className="size-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{objective.title}</p>
                  <p className="text-sm text-muted-foreground">{objective.description}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{objective.price}</p>
                </div>
                <ChevronRight className="size-5 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
